/**
 * Take a signed-out visitor back to where an email link was sending them.
 *
 * The "add a payment method" email links to the member's own billing page
 * (`/<club>/member/view-billing?tab=payment`). A parent who is not signed in
 * is redirected to the sign-in page, and sign-in always landed members on
 * the dashboard, so the link's promise ("straight to Payment Settings") was
 * lost exactly when it mattered.
 *
 * This remembers the destination across the sign-in redirect and hands it
 * back once.
 *
 * It is an ALLOW-LIST, not "go wherever the URL said": only the member pages
 * named below are ever remembered or returned, and only under the same club
 * the person signs in to, so this cannot be turned into an open redirect or
 * used to land someone on a page of another club.
 *
 * sessionStorage on purpose: it lives for this browser tab only, so a
 * destination can never leak into a later, unrelated sign-in.
 */

import { safeSessionGetItem, safeSessionRemoveItem, safeSessionSetItem } from "@mediatechgroup/app-version";

const STORAGE_KEY = "postLoginDestination";

/** Member pages a sign-in may return to. Path after `/<club>`. */
const ALLOWED_MEMBER_PATHS = [
  "/member/view-billing",
  // "View" buttons in camp and clinic emails (backend utils/webLinks.js).
  "/member/my-camps",
  "/member/my-clinics",
  // Support PIN setup email: the PIN panel is on the profile page.
  "/member/manage-profile",
];

/** Query parameters that may ride along, with the values each may take. */
const ALLOWED_QUERY: Record<string, string[]> = {
  tab: ["payment"],
  // Marks a link that came from an email, or from a text message (account
  // notices sent as a text: backend utils/webLinks.js `memberBilling`). It
  // carries no meaning to the page; it is what makes the address a
  // deliberate deep link (see sanitize).
  from: ["email", "text"],
};

function sanitize(pathname: string, search: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  const [slug, ...rest] = parts;
  if (!/^[a-z0-9][a-z0-9-]*$/i.test(slug)) return null;
  const memberPath = `/${rest.join("/")}`;
  if (!ALLOWED_MEMBER_PATHS.includes(memberPath)) return null;

  const incoming = new URLSearchParams(search || "");
  const kept = new URLSearchParams();
  Object.entries(ALLOWED_QUERY).forEach(([key, values]) => {
    const value = incoming.get(key);
    if (value && values.includes(value)) kept.set(key, value);
  });
  const query = kept.toString();
  // Only a deliberate deep link is worth remembering. A bare member page is
  // where someone simply happened to be when their session ended; sending
  // the next sign-in in this tab there would be a surprise.
  if (!query) return null;
  return `/${slug}${memberPath}?${query}`;
}

/**
 * Call when redirecting a signed-out visitor to sign-in. Remembers the page
 * only if it is an allow-listed deep link. Anything else is ignored (and
 * leaves an earlier remembered link alone: the redirect to the sign-in page
 * itself must not wipe it).
 */
export function rememberPostLoginDestination(pathname: string, search: string): void {
  try {
    const destination = sanitize(pathname, search);
    if (destination) safeSessionSetItem(STORAGE_KEY, destination);
  } catch {
    // Storage unavailable (private mode, full): the member lands on the
    // dashboard, as before.
  }
}

/**
 * Call once after a successful sign-in. Returns the remembered page if it
 * belongs to the club just signed in to, and forgets it either way.
 */
export function consumePostLoginDestination(tenantSlug: string | null | undefined): string | null {
  try {
    const stored = safeSessionGetItem(STORAGE_KEY);
    safeSessionRemoveItem(STORAGE_KEY);
    if (!stored || !tenantSlug) return null;
    const [pathname, search = ""] = stored.split("?");
    const destination = sanitize(pathname, search ? `?${search}` : "");
    if (!destination) return null;
    return destination === `/${tenantSlug}` || destination.startsWith(`/${tenantSlug}/`)
      ? destination
      : null;
  } catch {
    return null;
  }
}
